import { Field, FieldProps, FormikHelpers, FormikProps } from "formik";
import { Box, FormControl, FormLabel, Grid, Skeleton, Spacer } from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import React, { useCallback } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppInput from "@/features/ui/app-input";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import CreateCompanyModal, { CreateCompanyModalProps } from "@/features/ui/create-company-modal";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppInvoiceStatusLabel from "@/features/ui/app-invoice-status-label";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppPriceInput from "@/features/invoices/app-price-input";
import {
  OrderConfirmationInvoiceFormFieldsModal
} from "@/features/invoices/order-confirmation-invoices/upload-order-confirmation-invoice-page-modal";

interface OrderConfirmationInvoiceInformationFormControlPropsModal<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
}

interface FormFields extends OrderConfirmationInvoiceFormFieldsModal {}

export default function OrderConfirmationInvoiceInformationFormControlModal<
  T extends FormFields
>({
  formik,
  isEditing,
  isDisabled,
}: OrderConfirmationInvoiceInformationFormControlPropsModal<T>) {
  const identity = useIdentity();

  // 3자무역을 켜면 발행사를 3자무역 회사로 바꾸고 원래 부티크는 originallyIssuedBy 로 남긴다(프로포마와 같다).
  // 끄면 원래 부티크를 다시 발행사로 돌리고 수수료율을 비운다.
  const handleCrossTradeChange = useCallback(
    (checked: boolean) => {
      formik.setValues((prev) => {
        const boutique = prev.originallyIssuedBy || prev.issuedBy;
        if (checked) {
          return {
            ...prev,
            isCrossTrade: true,
            originallyIssuedBy: boutique,
          };
        }
        return {
          ...prev,
          isCrossTrade: false,
          issuedBy: boutique,
          originallyIssuedBy: undefined,
          crossTradeFeeRate: undefined,
        };
      });
    },
    [formik]
  );
  const crossTrader =
    formik.values.issuedBy?.type === "CROSS_TRADER"
      ? formik.values.issuedBy
      : undefined;
  const { t, tTitle } = useI18nHelper();

  type LINK_AFFECTING_KEYS =
    | "issuedBy"
    | "orderedBy"
    | "issuedThrough";

  const handleLinkAffectingFieldChange = useCallback(
    <T extends LINK_AFFECTING_KEYS>(
      key: LINK_AFFECTING_KEYS,
      value: FormFields[T],
      setValues: FormikHelpers<FormFields>["setValues"]
    ) => {
      return setValues((prev) => {
        let next = {
          ...prev,
          [key]: value,
          orderSheetSets: [],
        };

        next.orderConfirmationInvoiceDetailList =
          next.orderConfirmationInvoiceDetailList.map((ocDetail) => {
            return {
              ...ocDetail,
              brand: ocDetail.brand,
              orderSheetSet: undefined,
            };
          });

        return next;
      });
    },
    []
  );

  const {
    isOpen: isCreateCompanyModelOpen,
    onClose: onCreateCompanyModalClose,
    data: createCompanyModalData,
    openWithData: openCreateCompanyModal,
  } = useDisclosureWithData<CreateCompanyModalProps>();

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          rowGap={"8px"}
          gridTemplateRows={"repeat(7, 16px 24px) repeat(2, 16px 60px)"}
        >
          {(Array(18).fill(0) as number[]).map((_, index) => {
            return <Skeleton key={index} />;
          })}
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_information.abbr")}>
      <Grid rowGap={"8px"}>
        {!isEditing && (
          <>
            <Field name={"status"}>
              {(props: FieldProps<FormFields["status"], FormFields>) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"4px"}
                    isRequired={isEditing}
                    fontSize={"12px"}
                  >
                    <FormLabel
                      fontSize={"12px"}
                      color={"#6F6F6C"}
                      padding={"0"}
                      margin={"0"}
                    >
                      {toTitle(t("status"))}
                    </FormLabel>
                    <Spacer />
                    <AppInvoiceStatusLabel value={props.field.value} />
                  </FormControl>
                );
              }}
            </Field>

            <Field name={"isRemittedToBoutique"}>
              {(
                props: FieldProps<
                  FormFields["isRemittedToBoutique"],
                  FormFields
                >
              ) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"4px"}
                    isRequired={isEditing}
                    fontSize={"12px"}
                  >
                    <FormLabel
                      fontSize={"12px"}
                      color={"#6F6F6C"}
                      padding={"0"}
                      margin={"0"}
                    >
                      {toTitle(t("remittance_status_to_boutique"))}
                    </FormLabel>
                    <Spacer />
                    <AppRemittanceStatusLabel
                      value={
                        props.field.value !== undefined
                          ? props.field.value
                            ? "REMITTED"
                            : "UNREMITTED"
                          : undefined
                      }
                    />
                  </FormControl>
                );
              }}
            </Field>

            {formik.values.paidByAgency && (
              <Field name={"isRemittedToAgency"}>
                {(
                  props: FieldProps<
                    FormFields["isRemittedToAgency"],
                    FormFields
                  >
                ) => {
                  return (
                    <FormControl
                      display={"flex"}
                      flexDirection={"row"}
                      gap={"4px"}
                      isRequired={isEditing}
                      fontSize={"12px"}
                    >
                      <FormLabel
                        fontSize={"12px"}
                        color={"#6F6F6C"}
                        padding={"0"}
                        margin={"0"}
                      >
                        {toTitle(t("common:remittance_status_to_agency"))}
                      </FormLabel>
                      <Spacer />
                      <AppRemittanceStatusLabel
                        value={
                          props.field.value !== undefined
                            ? props.field.value
                              ? "REMITTED"
                              : "UNREMITTED"
                            : undefined
                        }
                      />
                    </FormControl>
                  );
                }}
              </Field>
            )}

            <Box
              height={"8px"}
              borderBottom={"0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)"}
            ></Box>
          </>
        )}

        <Field name={"name"}>
          {(props: FieldProps<FormFields["name"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("title"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"number"}>
          {(props: FieldProps<FormFields["number"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("invoice_number"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"issuedOn"}>
          {(props: FieldProps<Date, FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                isRequired={isEditing}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("issued_on"))}
                </FormLabel>
                <AppDatePicker
                  width={"100%"}
                  showTimeInput={false}
                  name={tTitle("issued_on")}
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  isDisabled={isDisabled}
                  onChange={(value) => {
                    props.form.setFieldValue("issuedOn", value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {/* 시즌 — 시즌 프로포마가 OC를 찾을 때 쓰던 값이라 생성·수정 모두 보여주고 고칠 수 있게 한다 */}
        <Field name={"season"}>
          {(props: FieldProps<FormFields["season"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("season"))}
                </FormLabel>
                <AppSeasonSelect
                  width={"100%"}
                  value={props.field.value}
                  isDisabled={isDisabled}
                  readonly={!isEditing}
                  onSelect={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type === "AGENCY" && (
          <Field name={"exposedToBuyer"}>
            {(props: FieldProps<FormFields["exposedToBuyer"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                >
                  <AppCheckBox
                    isChecked={props.field.value}
                    isDisabled={!isEditing}
                    name={toTitle(t("exposed_to_buyer"))}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        {/* 3자무역 — 고른 회사가 발행사가 되고 디파짓을 받는다. 디파짓은 수수료를 곱한 금액으로 발행한다. */}
        {identity?.company?.type !== "BOUTIQUE" && (
          <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
            <AppCheckBox
              isChecked={!!formik.values.isCrossTrade}
              isDisabled={!isEditing || isDisabled}
              name={"3자무역"}
              onChange={handleCrossTradeChange}
            />
          </FormControl>
        )}
        {identity?.company?.type !== "BOUTIQUE" && formik.values.isCrossTrade && (
          <>
            <FormControl
              display={"flex"}
              flexDirection={"column"}
              gap={"4px"}
              isRequired={isEditing}
              fontSize={"12px"}
            >
              <FormLabel
                fontSize={"12px"}
                color={"#6F6F6C"}
                padding={"0"}
                margin={"0"}
              >
                3자무역 회사
              </FormLabel>
              <AppCompanySelect
                name={"3자무역 회사"}
                types={["CROSS_TRADER"]}
                width={"100%"}
                id={crossTrader?.id}
                isDisabled={isDisabled}
                isReadOnly={!isEditing}
                onSelect={(value) => {
                  formik.setValues((prev) => {
                    const boutique = prev.originallyIssuedBy || prev.issuedBy;
                    return {
                      ...prev,
                      originallyIssuedBy: boutique,
                      issuedBy: value ?? boutique,
                    };
                  });
                }}
              />
            </FormControl>
            {/* 3자무역 수수료율은 더 이상 입력하지 않는다 — 인보이스 총액을 그대로 넣는다(2026-10-30) */}
          </>
        )}
      </Grid>
      {isCreateCompanyModelOpen && createCompanyModalData && (
        <CreateCompanyModal
          isOpen={isCreateCompanyModelOpen}
          onClose={onCreateCompanyModalClose}
          {...createCompanyModalData}
        />
      )}
    </AppPanel>
  );
}
